/**
 * Safely gets an element by checking if it exists first
 * @param {string} selector - CSS selector for the element
 * @returns {Element|null} - The element or null if not found
 */
const safeQuerySelector = (selector) => {
  try {
    return document.querySelector(selector);
  } catch (error) {
    console.warn(`Error finding element with selector: ${selector}`, error);
    return null;
  }
};

/**
 * Safely gets an element by ID by checking if it exists first
 * @param {string} id - ID of the element
 * @returns {Element|null} - The element or null if not found
 */
const safeGetElementById = (id) => {
  try {
    return document.getElementById(id);
  } catch (error) {
    console.warn(`Error finding element with ID: ${id}`, error);
    return null;
  }
};

/**
 * Smoothly scrolls to a section element identified by its ID
 * @param {string} sectionId - The ID of the target section to scroll to
 */
export const scrollToSection = (sectionId) => {
  if (typeof window === 'undefined' || !document) {
    console.warn('Browser environment not available');
    return;
  }

  try {
    let element = null;
    
    // Special case for resources section - target the heading instead of the section
    if (sectionId === 'resources') {
      element = safeQuerySelector('#resources h1');
    } 
    // Special case for about section - target the emblem instead of the section
    else if (sectionId === 'about') {
      element = safeQuerySelector('#about .logo-emblem-container');
    }
    // Special case for blog section - target the heading instead of the section
    else if (sectionId === 'blog') {
      element = safeQuerySelector('#blog h2');
    }
    else {
      element = safeGetElementById(sectionId);
    }
    
    if (!element) {
      console.warn(`Element with ID ${sectionId} not found`);
      return;
    }
    
    const navbar = safeQuerySelector('.navbar');
    const navbarHeight = navbar ? navbar.offsetHeight : 0;
    
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - navbarHeight - -30; // Increased offset for more scrolling

    window.scrollTo({
      top: offsetPosition,
      behavior: 'smooth'
    });
  } catch (error) {
    console.error('Error in scrollToSection:', error);
  }
};

export const createSectionObserver = (callback) => {
  if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
    return {
      observe: () => {},
      disconnect: () => {}
    };
  }

  const options = {
    root: null,
    rootMargin: '-80px 0px -20% 0px', // Adjust based on navbar height
    threshold: 0.1 // Change to 0.1 to be more forgiving
  };

  try {
    return new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting && entry.target && entry.target.id) {
          callback(entry.target.id);
        }
      });
    }, options);
  } catch (error) {
    console.error('Error creating intersection observer:', error);
    // Return a dummy observer if creation fails
    return {
      observe: () => {},
      disconnect: () => {}
    };
  }
};

export const initSmoothScrollPolyfill = () => {
  if (typeof window === 'undefined') return;
  
  if (!('scrollBehavior' in document.documentElement.style)) {
    try {
      import('smoothscroll-polyfill').then(smoothscroll => {
        smoothscroll.polyfill();
      }).catch(err => {
        console.warn('Failed to load smoothscroll polyfill', err);
      });
    } catch (error) {
      console.warn('Error initializing smooth scroll polyfill:', error);
    }
  }
}; 